import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'letwise_compliance_banner_dismissed';

// Pages where the banner should be shown
const ALLOWED_PATHS = [
  '/',
  '/home',
  '/for-landlords',
  '/landlord',
  '/landlords',
  '/landlord/dashboard',
  '/landlord/properties',
];

export default function ComplianceFloatingBanner() {
  const location = useLocation();
  
  // Compute visibility synchronously to avoid setState in effect
  const shouldShow = React.useMemo(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true') return false;
    const currentPath = location.pathname.toLowerCase();
    return ALLOWED_PATHS.some(path => 
      currentPath === path || currentPath.startsWith('/landlord')
    );
  }, [location.pathname]);
  
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!shouldShow) return;
    // Show banner after short delay
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);
    // Permanently dismiss - user won't see it again
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleLinkClick = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-300"
      data-testid="compliance-floating-banner"
    >
      <div className="bg-slate-800 text-white rounded-md shadow-md p-3 text-sm max-w-[280px]">
        <div className="flex items-center gap-2">
          <Link 
            to="/renters-rights-act"
            onClick={handleLinkClick}
            className="flex items-center gap-2 hover:underline flex-1"
            data-testid="compliance-banner-link"
          >
            <span>Renters&apos; Rights Act 2025</span>
            <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
          </Link>
          <button
            onClick={handleDismiss}
            className="p-0.5 hover:bg-slate-700 rounded transition-colors flex-shrink-0"
            aria-label="Dismiss banner"
            data-testid="compliance-banner-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
